// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback, useMemo } from 'react'
import { Loader2, AlertCircle, Plus, X, ShieldCheck, Trash2, ChevronRight, Copy } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import FieldAccessSection, { type FieldRule } from './FieldAccessSection'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

/**
 * Roles tab — list + create + edit.
 *
 * Editor uses a permission picker grouped by category. System roles are
 * read-only (the backend refuses edits and the UI greys out the form).
 */
export default function RolesTab() {
  const [items, setItems] = useState<RoleItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<{ id: string | null; cloneFrom?: string } | null>(null) // null=closed, {id:null}=new

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await api.apiFetch(`/roles`, { credentials: 'include' })
      if (!r.ok) throw await api.parseApiError(r)
      const data = await r.json()
      setItems(data.items)
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { reload() }, [reload])

  // D-UI-2: the header funnels filter the whole loaded list.
  const fx = useTableFunnels(items, ROLE_FUNNELS)

  return (
    <>
      <div style={toolbar}>
        <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
          {items.length} role{items.length === 1 ? '' : 's'}
        </span>
        <button
          onClick={() => setEditing({ id: null })}
          style={primaryBtn}
        >
          <Plus size={14} aria-hidden="true" />
          New role
        </button>
      </div>

      {loading ? (
        <div style={loadingWrap}>
          <Loader2 size={20} className="spin" />
          <span style={{ marginLeft: 10, color: 'var(--color-text-soft)' }}>Loading roles…</span>
        </div>
      ) : error ? (
        <div style={errorWrap}>
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : (
        <>
        {fx.chips}
        <div style={cardStyle}>
          <div style={tableHeader}>
            <div style={{ flex: 1.3 }}>Name {fx.funnel('name')}</div>
            <div style={{ flex: 2 }}>Description {fx.funnel('description')}</div>
            <div style={{ width: 70, textAlign: 'right' }}>Users {fx.funnel('users')}</div>
            <div style={{ width: 90, textAlign: 'right' }}>Permissions {fx.funnel('permissions')}</div>
            <div style={{ width: 80 }}>Type {fx.funnel('type')}</div>
            <div style={{ width: 24 }} aria-hidden="true" />
          </div>
          {fx.rows.map(r => (
            <button
              key={r.id}
              onClick={() => setEditing({ id: r.id })}
              style={tableRow}
            >
              <div style={{ flex: 1.3, fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                <ShieldCheck size={14} aria-hidden="true" style={{ color: 'var(--color-primary)' }} />
                <span>{r.display_name || r.name}</span>
              </div>
              <div style={{ flex: 2, fontSize: 12.5, color: 'var(--color-text-soft)', ...truncate }}>
                {r.description ?? <span style={{ color: 'var(--color-text-mute)' }}>—</span>}
              </div>
              <div style={{ width: 70, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                {r.user_count}
              </div>
              <div style={{ width: 90, textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                {r.permission_count}
              </div>
              <div style={{ width: 80 }}>
                <span style={{
                  ...typePill,
                  background: r.is_system ? '#dbeafe' : '#fef3c7',
                  color: r.is_system ? '#1e40af' : '#854d0e',
                }}>
                  {r.is_system ? 'System' : 'Custom'}
                </span>
              </div>
              <ChevronRight size={14} style={{ color: 'var(--color-text-mute)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
        </>
      )}

      {editing && (
        <RoleEditor
          key={editing.cloneFrom ? `clone-${editing.cloneFrom}` : (editing.id ?? 'new')}
          roleId={editing.id}
          cloneFrom={editing.cloneFrom}
          onClone={(sourceId) => setEditing({ id: null, cloneFrom: sourceId })}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}
    </>
  )
}

// ============================================================================
// Editor
// ============================================================================

function RoleEditor({ roleId, cloneFrom, onClone, onClose, onSaved }: {
  roleId: string | null
  cloneFrom?: string
  onClone: (sourceId: string) => void
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = roleId === null

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSystem, setIsSystem] = useState(false)
  const [perms, setPerms] = useState<Set<string>>(new Set())
  const [allPerms, setAllPerms] = useState<PermissionItem[]>([])
  const [userCount, setUserCount] = useState(0)
  const [phiOverride, setPhiOverride] = useState(false)
  const [fieldRules, setFieldRules] = useState<FieldRule[]>([])

  // Load all permissions once + role detail if editing
  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        const all = await api.apiFetch(`/permissions`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : Promise.reject(api.parseApiError(r)))
        if (cancelled) return
        setAllPerms(all.items)

        if (!isNew) {
          const r = await api.apiFetch(`/roles/${roleId}`, { credentials: 'include' })
          if (!r.ok) throw await api.parseApiError(r)
          const d = await r.json()
          if (cancelled) return
          setName(d.role.name)
          setDescription(d.role.description ?? '')
          setIsSystem(d.role.is_system)
          setPerms(new Set(d.permissions))
          setUserCount(d.users.length)
          setPhiOverride(!!d.role.phi_override)
          setLoading(false)
        } else if (cloneFrom) {
          // Clone: prefill a NEW (editable, custom) role from a source role's
          // permissions. Saves as a brand-new role, leaving the source untouched.
          const r = await api.apiFetch(`/roles/${cloneFrom}`, { credentials: 'include' })
          if (!r.ok) throw await api.parseApiError(r)
          const d = await r.json()
          if (cancelled) return
          setName(`Copy of ${d.role.name}`)
          setDescription(d.role.description ?? '')
          setIsSystem(false)
          setPerms(new Set(d.permissions))
          setPhiOverride(!!d.role.phi_override)
          setLoading(false)
        }
      } catch (e: any) {
        if (!cancelled) { setError(friendlyError(e)); setLoading(false) }
      }
    }
    init()
    return () => { cancelled = true }
  }, [roleId, isNew, cloneFrom])

  // Escape closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const grouped = useMemo(() => {
    const out: Record<string, PermissionItem[]> = {}
    for (const p of allPerms) {
      const cat = p.category ?? 'other'
      if (!out[cat]) out[cat] = []
      out[cat].push(p)
    }
    return out
  }, [allPerms])

  function togglePerm(key: string) {
    if (isSystem) return
    setPerms(prev => {
      const n = new Set(prev)
      if (n.has(key)) n.delete(key); else n.add(key)
      return n
    })
  }

  function toggleCategory(cat: string, on: boolean) {
    if (isSystem) return
    setPerms(prev => {
      const n = new Set(prev)
      for (const p of grouped[cat]) {
        if (on) n.add(p.key); else n.delete(p.key)
      }
      return n
    })
  }

  async function save() {
    if (!isSystem && !name.trim()) { setError('Role name is required.'); return }

    setSaving(true); setError(null)
    try {
      let targetId = roleId
      // System roles: name + permission set stay locked, but field-access rules ARE editable.
      // Only create/update the role record for non-system roles.
      if (!isSystem) {
        const body = {
          name: name.trim(),
          description: description.trim() || null,
          permissions: Array.from(perms),
          phi_override: phiOverride,
        }
        const res = isNew
          ? await api.apiFetch(`/roles`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
            })
          : await api.apiFetch(`/roles/${roleId}`, {
              method: 'PUT', credentials: 'include',
              headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
            })
        if (!res.ok) throw await api.parseApiError(res)
        if (isNew) {
          try { const created = await res.json(); targetId = created.id ?? created.role?.id ?? null } catch { /* ignore */ }
        }
      }

      // Persist field-access rules for ANY role, including system roles (the backend allows
      // it — this is how a built-in role is granted/masked on specific fields, e.g. a
      // deny-by-default custom field).
      if (targetId) {
        const fr = await api.apiFetch(`/roles/${targetId}/field-rules`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rules: fieldRules }),
        })
        if (!fr.ok) throw await api.parseApiError(fr)
      }
      onSaved()
      onClose()
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setSaving(false)
    }
  }

  async function deleteRole() {
    if (isSystem || isNew) return
    if (userCount > 0) {
      alert(`Can't delete: ${userCount} user(s) still hold this role. Revoke from those users first.`)
      return
    }
    if (!confirm(`Delete role "${name}" permanently? This can't be undone.`)) return
    setSaving(true); setError(null)
    try {
      const res = await api.apiFetch(`/roles/${roleId}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw await api.parseApiError(res)
      onSaved()
      onClose()
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div style={backdropStyle} onClick={onClose} aria-hidden="true" />
      <aside style={drawerStyle} role="dialog" aria-label={isNew ? 'New role' : 'Edit role'}>
        <header style={drawerHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            {isNew ? 'New role' : 'Edit role'}
            {isSystem && <span style={{ ...miniBadge, marginLeft: 8 }}>system · name &amp; permissions locked · field access editable</span>}
          </h3>
          <button onClick={onClose} style={iconBtn} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        {isSystem && !isNew && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--color-text-soft)' }}>
              Name &amp; permissions are locked on system roles. To get a fully editable role with the same permissions:
            </span>
            <button onClick={() => onClone(roleId!)} style={cloneBtn}>
              <Copy size={13} aria-hidden="true" /> Clone to editable role
            </button>
          </div>
        )}

        {loading ? (
          <div style={loadingWrap}><Loader2 size={20} className="spin" /></div>
        ) : (
          <div style={drawerBody}>
            {error && (
              <div style={errorWrap}>
                <AlertCircle size={16} aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label style={labelStyle} htmlFor="role-name">Name</label>
              <input
                id="role-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={isSystem || saving}
                style={inputStyle}
                maxLength={64}
              />
            </div>

            <div>
              <label style={labelStyle} htmlFor="role-desc">Description</label>
              <textarea
                id="role-desc"
                value={description}
                onChange={e => setDescription(e.target.value)}
                disabled={isSystem || saving}
                rows={2}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                maxLength={500}
              />
            </div>

            <div>
              <label style={labelStyle}>
                Permissions ({perms.size} selected)
              </label>
              <div style={permGroupsWrap}>
                {Object.entries(grouped).map(([cat, ps]) => {
                  const allOn = ps.every(p => perms.has(p.key))
                  return (
                    <div key={cat} style={permGroup}>
                      <div style={permGroupHeader}>
                        <span style={permGroupTitle}>{cat.toUpperCase()}</span>
                        <button
                          onClick={() => toggleCategory(cat, !allOn)}
                          disabled={isSystem || saving}
                          style={permGroupToggleBtn}
                          type="button"
                        >
                          {allOn ? 'Clear all' : 'Select all'}
                        </button>
                      </div>
                      <ul style={permList}>
                        {ps.map(p => (
                          <li key={p.key} style={permRow}>
                            <label style={permLabelRow}>
                              <input
                                type="checkbox"
                                checked={perms.has(p.key)}
                                onChange={() => togglePerm(p.key)}
                                disabled={isSystem || saving}
                                style={{ marginRight: 8 }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <code style={codeStyle}>{p.key}</code>
                                <div style={permDesc}>{p.display_name}</div>
                              </div>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            </div>

            {!isSystem && (
              <div style={{ marginTop: 16, padding: 12, border: '1px solid var(--color-border)', borderRadius: 8 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={phiOverride}
                    disabled={saving}
                    onChange={e => setPhiOverride(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Override PHI visibility from other roles</span>
                    <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
                      When a user holds this role, its PHI masking wins even if another of their roles
                      (e.g. Provider) would normally see PHI. Affects PHI fields only — every other
                      permission stays as granted. With this on and no field set to <strong>Full</strong> below,
                      all PHI is masked automatically.
                    </p>
                  </span>
                </label>
              </div>
            )}

            {!isSystem && (
              <FieldAccessSection
                roleId={isNew ? null : roleId}
                disabled={saving}
                onRulesChange={setFieldRules}
              />
            )}
          </div>
        )}

        <footer style={drawerFooter}>
          <div>
            {!isNew && !isSystem && (
              <button onClick={deleteRole} disabled={saving} style={dangerBtn}>
                <Trash2 size={14} aria-hidden="true" />
                Delete role
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving} style={secondaryBtn}>Cancel</button>
            {!isSystem && (
              <button onClick={save} disabled={saving} style={primaryBtn}>
                {saving ? <Loader2 size={14} className="spin" /> : null}
                {saving ? 'Saving…' : (isNew ? 'Create role' : 'Save changes')}
              </button>
            )}
          </div>
        </footer>
      </aside>
    </>
  )
}

// ============================================================================
// Types
// ============================================================================

interface RoleItem {
  id: string
  name: string
  description: string | null
  is_system: boolean
  user_count: number
  permission_count: number
  display_name: string | null
  phi_override?: boolean
}

// D-UI-2: what each column of the roles list shows, for its header funnel.
const ROLE_FUNNELS: FunnelColumn<RoleItem>[] = [
  { key: 'name',        label: 'Name',        value: r => r.display_name || r.name },
  { key: 'description', label: 'Description', value: r => r.description, kind: 'text' },
  { key: 'users',       label: 'Users',       value: r => r.user_count },
  { key: 'permissions', label: 'Permissions', value: r => r.permission_count },
  { key: 'type',        label: 'Type',        value: r => r.is_system ? 'System' : 'Custom' },
]

interface PermissionItem {
  key: string
  display_name: string
  description: string | null
  category: string | null
}

// ============================================================================
// Styles (intentional duplication with UsersTab — these are local; once we
// have 3+ admin tabs we can extract to a shared module.)
// ============================================================================

const toolbar: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16,
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden',
}
const tableHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', textTransform: 'uppercase',
}
const tableRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 14px', width: '100%',
  background: 'transparent',
  border: 'none', borderBottom: '1px solid var(--color-border)',
  fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
}
const truncate: React.CSSProperties = {
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}
const typePill: React.CSSProperties = {
  display: 'inline-block', fontSize: 10, fontWeight: 600,
  padding: '2px 8px', borderRadius: 10, letterSpacing: 0.5,
}
const loadingWrap: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60,
}
const errorWrap: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '12px 16px',
  background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6,
  color: '#991b1b', fontSize: 13,
}
const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.4)', zIndex: 50,
}
const drawerStyle: React.CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0,
  width: 'min(580px, 100vw)',
  background: 'var(--color-surface, #fff)',
  borderLeft: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.08))',
  zIndex: 51,
  display: 'flex', flexDirection: 'column',
}
const drawerHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 16px',
  borderBottom: '1px solid var(--color-border)',
}
const drawerBody: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: 16,
  display: 'flex', flexDirection: 'column', gap: 16,
}
const drawerFooter: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '12px 16px',
  borderTop: '1px solid var(--color-border)',
  background: 'var(--color-surface-alt)',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', textTransform: 'uppercase', marginBottom: 6,
}
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit',
  background: 'var(--color-surface)',
}
const permGroupsWrap: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 12,
}
const permGroup: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  background: 'var(--color-surface-alt)',
}
const permGroupHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '8px 12px',
  borderBottom: '1px solid var(--color-border)',
}
const permGroupTitle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: 'var(--color-text)',
}
const permGroupToggleBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  fontSize: 11,
  color: 'var(--color-primary)',
  cursor: 'pointer',
  fontFamily: 'inherit',
}
const permList: React.CSSProperties = {
  margin: 0, padding: 0, listStyle: 'none',
  background: 'var(--color-surface, #fff)',
}
const permRow: React.CSSProperties = {
  borderBottom: '1px solid var(--color-border)',
}
const permLabelRow: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start',
  padding: '8px 12px',
  cursor: 'pointer',
}
const codeStyle: React.CSSProperties = {
  background: 'var(--color-surface-alt)',
  padding: '1px 5px', borderRadius: 3,
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11.5, color: 'var(--color-text)',
}
const permDesc: React.CSSProperties = {
  fontSize: 11.5, color: 'var(--color-text-soft)',
  marginTop: 2,
}
const miniBadge: React.CSSProperties = {
  fontSize: 10, fontWeight: 600,
  background: '#dbeafe', color: '#1e40af',
  padding: '1px 6px', borderRadius: 8, letterSpacing: 0.5,
}
const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none',
  padding: 6, borderRadius: 6, cursor: 'pointer',
  color: 'var(--color-text-mute)',
}
const cloneBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600,
  padding: '5px 10px', borderRadius: 7, border: '1px solid var(--color-primary)',
  background: 'var(--color-primary)', color: '#fff', cursor: 'pointer',
}
const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px',
  background: 'var(--color-primary)', color: '#fff',
  border: 'none', borderRadius: 6,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
  marginLeft: 'auto',
}
const secondaryBtn: React.CSSProperties = {
  padding: '8px 14px',
  background: 'transparent',
  color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
}
const dangerBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px',
  background: '#fee2e2', color: '#991b1b',
  border: '1px solid #fecaca', borderRadius: 6,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
}
